/**
 * The trail back to the front door, on every page that is not the front door.
 *
 * Every page used to build its own `<Breadcrumbs>`, and they drifted exactly
 * as you would expect: the ticket form started at "My tickets" with no way
 * home, the approval detail used plain `href`s — full page reloads in a SPA —
 * and four pages had no trail at all. This exists so a page states *where it
 * sits* and nothing else.
 *
 * Two rules it enforces, because they are the ones that were being broken:
 *
 *   - **Home is always first**, and is `/ask`. The conversation is the front
 *     door of this product, so "Home" has to mean the same thing everywhere.
 *   - **Every crumb with a `to` is a React Router `<Link>`.** The design
 *     system takes a `render` prop for this; forgetting it silently produces
 *     an `<a href>`, which throws away the SPA and every bit of cached state
 *     with it. Getting that wrong is invisible until someone notices the page
 *     flashing white on a breadcrumb click.
 *
 * The last crumb is the current page: no link, marked `current` for assistive
 * tech, and never repeated in the heading below it.
 */

import { Breadcrumbs } from "@norican/react";
import { Link } from "react-router";

import { useT } from "@/i18n";

export interface AppCrumb {
  label: string;
  /** Omit on the final crumb — the page you are already on. */
  to?: string;
}

export interface AppBreadcrumbsProps {
  /** The trail *after* Home, outermost first. */
  items: AppCrumb[];
}

export function AppBreadcrumbs({ items }: AppBreadcrumbsProps) {
  const { t } = useT();

  const trail: AppCrumb[] = [{ label: t("nav.home"), to: "/ask" }, ...items];

  return (
    <Breadcrumbs
      items={trail.map((crumb, index) => {
        const isLast = index === trail.length - 1;
        if (crumb.to === undefined || isLast) {
          return { label: crumb.label, current: isLast };
        }
        const to = crumb.to;
        return {
          label: crumb.label,
          href: to,
          render: ({ className, children }) => (
            <Link to={to} className={className}>
              {children}
            </Link>
          ),
        };
      })}
    />
  );
}
